import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Loader2, Target, CheckCircle2, XOctagon } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { SWATCHES } from "./swatches";

export function NewStageSheet({
  open,
  onOpenChange,
  pipelineId,
  nextPosition,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  pipelineId: number;
  nextPosition: number;
  onCreated?: () => void;
}) {
  const [name, setName] = useState("");
  const [color, setColor] = useState(SWATCHES[3]);
  const [probability, setProbability] = useState(50);
  const [kind, setKind] = useState<"normal" | "won" | "lost">("normal");
  const utils = trpc.useUtils();
  const create = trpc.crm.stages.create.useMutation({
    onSuccess: () => {
      toast.success("Estágio criado");
      utils.crm.stages.list.invalidate({ pipelineId });
      onOpenChange(false);
      setName("");
      setColor(SWATCHES[3]);
      setProbability(50);
      setKind("normal");
      onCreated?.();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao criar estágio")),
  });

  useEffect(() => {
    if (kind === "won") {
      setProbability(100);
      setColor("#22c55e");
    } else if (kind === "lost") {
      setProbability(0);
      setColor("#ef4444");
    }
  }, [kind]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[420px] p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle>Novo estágio</SheetTitle>
          <SheetDescription>Adicione uma etapa ao funil</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <Label className="text-xs">Nome</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Qualificação"
              className="mt-1"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs">Tipo de estágio</Label>
            <div className="grid grid-cols-3 gap-1.5 mt-2">
              {(
                [
                  { key: "normal" as const, label: "Normal", icon: Target },
                  { key: "won" as const, label: "Fechado", icon: CheckCircle2 },
                  { key: "lost" as const, label: "Perdido", icon: XOctagon },
                ]
              ).map((k) => {
                const active = kind === k.key;
                const Icon = k.icon;
                return (
                  <button
                    key={k.key}
                    type="button"
                    onClick={() => setKind(k.key)}
                    className={`flex items-center justify-center gap-1.5 rounded-md border px-2.5 py-2 text-sm transition ${
                      active ? "border-primary bg-primary/5" : "hover:bg-muted/40"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span>{k.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs flex items-center justify-between">
              <span>Cor</span>
            </Label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {SWATCHES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`h-7 w-7 rounded-md hover:scale-110 transition ${
                    color.toLowerCase() === c.toLowerCase() ? "ring-2 ring-foreground ring-offset-1" : ""
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          {kind === "normal" && (
            <div>
              <Label className="text-xs flex items-center justify-between">
                <span>Probabilidade de fechamento</span>
                <span className="tabular-nums text-muted-foreground">{probability}%</span>
              </Label>
              <Slider
                value={[probability]}
                min={0}
                max={100}
                step={5}
                onValueChange={(v) => setProbability(v[0])}
                className="mt-3"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Usada para previsão ponderada de receita em relatórios futuros.
              </p>
            </div>
          )}
        </div>
        <SheetFooter className="border-t px-6 py-4 bg-muted/30">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              if (!name.trim()) return toast.error("Nome obrigatório");
              create.mutate({
                pipelineId,
                name: name.trim(),
                position: nextPosition,
                color,
                probability,
                isWon: kind === "won",
                isLost: kind === "lost",
              });
            }}
            disabled={create.isPending}
          >
            {create.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Adicionar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
